import React, { useMemo, useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Popconfirm,
  Form,
  Select,
  DatePicker,
  TimePicker,
  InputNumber,
  Input,
  message,
  Tag,
  Card,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  FilterOutlined,
  ClearOutlined,
  DeleteOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { aulaDadaService, turmaService, equipeService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissoes } from '../hooks/usePermissoes';
import dayjs from 'dayjs';
import ShortId from './common/ShortId';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const AulasDadasList = () => {
  const { user } = useAuth();
  const { permissoes } = usePermissoes();
  const [aulas, setAulas] = useState([]);
  const [aulasFiltradas, setAulasFiltradas] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isValidateModalVisible, setIsValidateModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [validatingAula, setValidatingAula] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [validateForm] = Form.useForm();
  const [stats, setStats] = useState({
    pendentes: 0,
    validadas: 0,
    rejeitadas: 0,
  });

  // Filtros
  const [filtros, setFiltros] = useState({
    professor: null,
    turma: null,
    status: null,
    data_inicial: null,
    data_final: null,
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(true);

  const isAdminOrRH = ['ADMIN', 'RH'].includes(user?.role);
  const isAdmin = user?.role === 'ADMIN';

  const normalizeListPayload = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.results)) return payload.results;
    if (Array.isArray(payload?.data?.items)) return payload.data.items;
    if (Array.isArray(payload?.data?.results)) return payload.data.results;
    return [];
  };

  const fetchAulas = async (filtrosAtivos = filtros) => {
    setLoading(true);
    try {
      console.error('[FETCH_ABORTADO] AulasDadasList', {
        user,
        role: user?.role,
        id_perfil: user?.id_perfil,
        permissoes,
        recursos: permissoes?.recursos,
      });
      const limit = 500;
      let skip = 0;
      let todas = [];

      const paramsBase = {};
      if (filtrosAtivos.turma) {
        paramsBase.id_turma = filtrosAtivos.turma;
      }
      if (filtrosAtivos.professor) {
        paramsBase.id_equipe = filtrosAtivos.professor;
      }
      if (filtrosAtivos.status) {
        paramsBase.status = filtrosAtivos.status;
      }
      if (filtrosAtivos.data_inicial) {
        paramsBase.data_inicial = filtrosAtivos.data_inicial;
      }
      if (filtrosAtivos.data_final) {
        paramsBase.data_final = filtrosAtivos.data_final;
      }

      while (true) {
        const params = { ...paramsBase, limit, skip };
        console.log('[ANTES_DO_GET] /api/aulas-dadas/', params);
        const response = await aulaDadaService.list(params);
        const lote = normalizeListPayload(response?.data);
        todas = todas.concat(lote);
        if (lote.length < limit) break;
        skip += limit;
      }

      const unicosPorId = new Map();
      todas.forEach((item) => {
        if (item && item.id !== undefined && item.id !== null) {
          unicosPorId.set(item.id, item);
        }
      });

      const ordered = [...unicosPorId.values()].sort(
        (a, b) => dayjs(a.data_aula).unix() - dayjs(b.data_aula).unix()
      );

      const resultadoFinal = ordered;

      if (resultadoFinal.length > 0) {
        const primeiraAula = resultadoFinal[0];
        console.log('[AULAS_DADAS_DEBUG] primeira aula recebida', {
          id: primeiraAula?.id,
          id_turma: primeiraAula?.id_turma,
          id_equipe: primeiraAula?.id_equipe,
          keys: Object.keys(primeiraAula || {}),
        });
      } else {
        console.log('[AULAS_DADAS_DEBUG] nenhuma aula recebida');
      }

      setAulas(ordered);
      setAulasFiltradas(resultadoFinal);
      calculateStats(resultadoFinal);
    } catch (error) {
      console.error('[FETCH_ABORTADO] AulasDadasList', error);
      message.error('Erro ao carregar aulas');
    }
    setLoading(false);
  };

  const handleFiltroChange = (campo, valor) => {
    const novosFiltros = { ...filtros, [campo]: valor };
    
    // Se mudar o professor, limpar o filtro de turma
    if (campo === 'professor') {
      novosFiltros.turma = null;
    }
    
    setFiltros(novosFiltros);
    fetchAulas(novosFiltros);
  };

  const limparFiltros = () => {
    const filtrosLimpos = {
      professor: null,
      turma: null,
      status: null,
      data_inicial: null,
      data_final: null,
    };
    setFiltros(filtrosLimpos);
    fetchAulas(filtrosLimpos);
  };

  const handlePeriodoChange = (dates) => {
    const novosFiltros = {
      ...filtros,
      data_inicial: dates?.[0] ? dates[0].format('YYYY-MM-DD') : null,
      data_final: dates?.[1] ? dates[1].format('YYYY-MM-DD') : null,
    };

    setFiltros(novosFiltros);
    fetchAulas(novosFiltros);
  };

  const getTurmasPorProfessorFiltro = () => {
    if (!filtros.professor) return turmas;
    return turmas.filter((t) => t.id_equipe === filtros.professor);
  };

  const calculateStats = (aulasData) => {
    const stats = {
      pendentes: aulasData.filter((a) => a.status === 'PENDENTE').length,
      validadas: aulasData.filter((a) => a.status === 'VALIDADA').length,
      rejeitadas: aulasData.filter((a) => a.status === 'REJEITADA').length,
    };
    setStats(stats);
  };

  const fetchTurmas = async () => {
    console.log('[AULAS_DADAS_DEBUG] fetchTurmas iniciado');
    try {
      const limit = 200;
      let skip = 0;
      const acumulado = [];

      while (true) {
        const response = await turmaService.list({ include_totais: false, limit, skip });
        const payload = response?.data;
        const items = normalizeListPayload(payload);

        console.log('[AULAS_DADAS_DEBUG] resposta bruta GET /api/turmas/?include_totais=false', {
          skip,
          responseData: response?.data,
        });

        console.log('[AULAS_DADAS_DEBUG] catálogo turmas (lote)', {
          skip,
          quantidadeTurmasCarregadas: items.length,
          primeiraTurmaCarregada: items?.[0] || null,
          keysPrimeiraTurma: items?.[0] ? Object.keys(items[0]) : [],
        });

        console.log('[AULAS_DADAS_DEBUG] lote turmas', {
          skip,
          qtd: items.length,
          payloadKeys: payload && typeof payload === 'object' ? Object.keys(payload) : null,
          primeiroItem: items?.[0],
        });

        acumulado.push(...items);

        if (items.length < limit) {
          break;
        }

        skip += limit;
      }

      setTurmas(acumulado);
      console.log('[AULAS_DADAS_DEBUG] turmas acumuladas', {
        total: acumulado.length,
        primeiroId: acumulado?.[0]?.id,
        primeiroNome: acumulado?.[0]?.nome_turm || acumulado?.[0]?.nome,
      });
    } catch (error) {
      console.error('[AULAS_DADAS_DEBUG] fetchTurmas erro', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
    }
  };

  const fetchProfessores = async () => {
    console.log('[AULAS_DADAS_DEBUG] fetchEquipe iniciado');
    try {
      const response = await equipeService.list();
      setProfessores(response.data);
      console.log('[AULAS_DADAS_DEBUG] fetchEquipe finalizado', {
        quantidade: Array.isArray(response?.data) ? response.data.length : null,
      });
    } catch (error) {
      console.error('[AULAS_DADAS_DEBUG] fetchEquipe erro', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
    }
  };

  useEffect(() => {
    let active = true;

    const loadInicial = async () => {
      console.log('[AULAS_DADAS_DEBUG] load inicial iniciado');

      const results = await Promise.allSettled([
        fetchAulas(),
        fetchTurmas(),
        fetchProfessores(),
      ]);

      if (!active) return;

      console.log('[AULAS_DADAS_DEBUG] load inicial finalizado', {
        aulasStatus: results[0]?.status,
        turmasStatus: results[1]?.status,
        equipeStatus: results[2]?.status,
      });
    };

    loadInicial();

    return () => {
      active = false;
    };
  }, []);

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      ...record,
      data_aula: record.data_aula ? dayjs(record.data_aula) : null,
      hora_inicio: record.hora_inicio
        ? dayjs(record.hora_inicio, 'HH:mm:ss')
        : null,
      hora_fim: record.hora_fim ? dayjs(record.hora_fim, 'HH:mm:ss') : null,
    });
    setIsModalVisible(true);
  };

  const handleProfessorChange = (professorId) => {
    // Limpar turma selecionada e horários ao mudar professor
    form.setFieldsValue({
      id_turma: null,
      hora_inicio: null,
      hora_fim: null,
      duracao_horas: null,
    });
  };

  const handleTurmaChange = (turmaId) => {
    const turmaSelecionada = turmas.find((t) => t.id === turmaId);
    if (turmaSelecionada) {
      // Auto-preencher horários da turma
      const horaInicio = turmaSelecionada.hora_inicio
        ? dayjs(turmaSelecionada.hora_inicio, 'HH:mm:ss')
        : null;
      const horaFim = turmaSelecionada.hora_fim
        ? dayjs(turmaSelecionada.hora_fim, 'HH:mm:ss')
        : null;

      form.setFieldsValue({
        hora_inicio: horaInicio,
        hora_fim: horaFim,
      });

      // Calcular duração automaticamente
      if (horaInicio && horaFim) {
        const diffMinutes = horaFim.diff(horaInicio, 'minute');
        const diffHoras = diffMinutes / 60;
        form.setFieldsValue({ duracao_horas: diffHoras });
      }
    }
  };

  const handleHorarioChange = () => {
    const horaInicio = form.getFieldValue('hora_inicio');
    const horaFim = form.getFieldValue('hora_fim');

    if (horaInicio && horaFim) {
      const diffMinutes = horaFim.diff(horaInicio, 'minute');
      if (diffMinutes > 0) {
        const diffHoras = diffMinutes / 60;
        form.setFieldsValue({ duracao_horas: diffHoras });
      }
    }
  };

  const getTurmasPorProfessor = (professorId) => {
    if (!professorId) return [];
    return turmas.filter((t) => t.id_equipe === professorId);
  };

  const handleSubmit = async (values) => {
    try {
      const submitData = {
        ...values,
        data_aula: values.data_aula.format('YYYY-MM-DD'),
        hora_inicio: values.hora_inicio
          ? values.hora_inicio.format('HH:mm:ss')
          : null,
        hora_fim: values.hora_fim ? values.hora_fim.format('HH:mm:ss') : null,
      };

      if (editingId) {
        await aulaDadaService.update(editingId, submitData);
        message.success('Aula atualizada com sucesso');
      } else {
        await aulaDadaService.create(submitData);
        message.success('Aula registrada com sucesso');
      }
      setIsModalVisible(false);
      fetchAulas();
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail))
        : 'Erro ao salvar aula';
      message.error(errorMsg);
    }
  };

  const handleValidate = (record) => {
    setValidatingAula(record);
    validateForm.setFieldsValue({
      status: undefined,
      observacoes: undefined,
      hora_inicio: record?.hora_inicio ? dayjs(record.hora_inicio, 'HH:mm:ss') : null,
      hora_fim: record?.hora_fim ? dayjs(record.hora_fim, 'HH:mm:ss') : null,
    });
    setIsValidateModalVisible(true);
  };

  const handleValidateSubmit = async (values) => {
    try {
      if (values.status === 'VALIDAR_AJUSTAR') {
        await aulaDadaService.ajustarHorario(validatingAula.id, {
          hora_inicio: values.hora_inicio.format('HH:mm:ss'),
          hora_fim: values.hora_fim.format('HH:mm:ss'),
        });
        await aulaDadaService.validar(validatingAula.id, {
          status: 'VALIDADA',
          observacoes: values.observacoes,
        });
        message.success('Aula validada com ajuste de horário');
      } else {
        await aulaDadaService.validar(validatingAula.id, values);
        message.success(
          values.status === 'VALIDADA'
            ? 'Aula validada com sucesso'
            : 'Aula rejeitada'
        );
      }
      setIsValidateModalVisible(false);
      fetchAulas();
    } catch (error) {
      message.error('Erro ao validar aula');
    }
  };

  const handleDelete = async (id) => {
    try {
      await aulaDadaService.delete(id);
      message.success('Aula excluída com sucesso');
      fetchAulas();
    } catch (error) {
      message.error('Erro ao excluir aula');
    }
  };

  const handleBulkDelete = () => {
    Modal.confirm({
      title: 'Excluir aulas selecionadas',
      content: `Deseja excluir ${selectedRowKeys.length} aulas selecionadas?`,
      okText: 'Excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        const totalSelecionadas = selectedRowKeys.length;
        for (const id of selectedRowKeys) {
          await aulaDadaService.delete(id);
        }
        message.success(`${totalSelecionadas} aulas excluídas com sucesso`);
        setSelectedRowKeys([]);
        fetchAulas();
      },
    });
  };

  const handleDeletePendentesTurma = async () => {
    if (!filtros.turma) {
      message.warning('Selecione uma turma para apagar as aulas pendentes');
      return;
    }

    setBulkDeleting(true);
    try {
      const response = await aulaDadaService.deletePendentesTurma(filtros.turma);
      const data = response?.data;
      message.success(
        `Aulas pendentes apagadas. Mantidas validadas: ${data?.mantidas_validas ?? 0}`
      );
      fetchAulas();
    } catch (error) {
      const errorMsg = error.response?.data?.detail
        ? (typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : JSON.stringify(error.response.data.detail))
        : 'Erro ao apagar aulas pendentes da turma';
      message.error(errorMsg);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleRevert = async (record) => {
    try {
      await aulaDadaService.reverter(record.id);
      message.success('Validação revertida com sucesso');
      fetchAulas();
    } catch (error) {
      const errorMsg = error.response?.data?.detail
        ? (typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : JSON.stringify(error.response.data.detail))
        : 'Erro ao reverter validação';
      message.error(errorMsg);
    }
  };

  const turmasById = useMemo(() => {
    const map = new Map();
    turmas.forEach((t) => {
      const key = String(t?.id || '').trim().toLowerCase();
      if (!key) return;
      map.set(key, t?.nome_turm || t?.nome || '-');
    });
    return map;
  }, [turmas]);

  const getTurmaName = (id) => {
    const key = String(id || '').trim().toLowerCase();
    if (!key) return '-';
    return turmasById.get(key) || '-';
  };

  const getTurmaNameFromRecord = (record) => {
    const possibleIds = [
      record?.id_turma,
      record?.idTurma,
      record?.turma_id,
      record?.turma?.id,
    ];

    for (const candidate of possibleIds) {
      const resolved = getTurmaName(candidate);
      if (resolved !== '-') return resolved;
    }

    return '-';
  };

  useEffect(() => {
    if (!aulas.length) return;
    if (!turmas.length) return;
    const firstAulaTurma = String(aulas[0]?.id_turma || '').trim().toLowerCase();
    const targetTurmaId = '3cb50676-9203-45dc-83bf-aa28e4600c15';
    const targetTurmaKey = targetTurmaId.toLowerCase();
    console.log('[AULAS_DADAS_DEBUG] map containment', {
      firstAulaId: aulas[0]?.id,
      firstAulaIdTurma: aulas[0]?.id_turma,
      firstAulaTurmaKeyNormalized: firstAulaTurma,
      turmasMapSize: turmasById.size,
      containsFirstAulaTurma: turmasById.has(firstAulaTurma),
      resolvedName: turmasById.get(firstAulaTurma) || '-',
      mapHasTargetTurmaId: turmasById.has(targetTurmaKey),
      mapGetTargetTurmaId: turmasById.get(targetTurmaKey) || '-',
    });
  }, [aulas, turmas, turmasById]);

  useEffect(() => {
    if (!turmas.length) return;
    const targetTurmaKey = '3cb50676-9203-45dc-83bf-aa28e4600c15';
    console.log('[AULAS_DADAS_DEBUG] map containment target only', {
      turmasMapSize: turmasById.size,
      mapHasTargetTurmaId: turmasById.has(targetTurmaKey),
      mapGetTargetTurmaId: turmasById.get(targetTurmaKey) || '-',
    });
  }, [turmas, turmasById]);

  const getProfessorName = (id) => {
    const professor = professores.find((p) => p.id === id);
    return professor ? professor.nome : '-';
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      PENDENTE: { color: 'gold', text: 'Pendente' },
      VALIDADA: { color: 'green', text: 'Validada' },
      REJEITADA: { color: 'red', text: 'Rejeitada' },
    };
    const config = statusConfig[status] || statusConfig.PENDENTE;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      render: (id) => <ShortId id={id} />,
      sorter: (a, b) => String(a?.id || '').localeCompare(String(b?.id || ''), 'pt-BR', { sensitivity: 'base' }),
    },
    {
      title: 'Data',
      dataIndex: 'data_aula',
      key: 'data_aula',
      sorter: (a, b) => dayjs(a.data_aula).unix() - dayjs(b.data_aula).unix(),
      render: (date) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Turma',
      dataIndex: 'id_turma',
      key: 'id_turma',
      render: (id, record) => {
        const byDataIndex = getTurmaName(id);
        if (byDataIndex !== '-') return byDataIndex;

        const byRecordFallback = getTurmaNameFromRecord(record);
        if (byRecordFallback !== '-') return byRecordFallback;

        console.log('[AULAS_DADAS_DEBUG] turma não resolvida no render', {
          idRecebidoNoRender: id,
          record,
        });

        return '-';
      },
    },
    {
      title: 'Professor',
      dataIndex: 'id_equipe',
      key: 'id_equipe',
      render: (id) => getProfessorName(id),
    },
    {
      title: 'Horário',
      key: 'horario',
      render: (_, record) => {
        if (record.hora_inicio && record.hora_fim) {
          return `${dayjs(record.hora_inicio, 'HH:mm:ss').format('HH:mm')} - ${dayjs(record.hora_fim, 'HH:mm:ss').format('HH:mm')}`;
        }
        return '-';
      },
    },
    {
      title: 'Duração (h)',
      dataIndex: 'duracao_horas',
      key: 'duracao_horas',
      render: (h) => (h ? h.toFixed(2) : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          {record.status === 'PENDENTE' && isAdminOrRH && (
            <>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                size="small"
                onClick={() => handleValidate(record)}
              >
                Validar
              </Button>
            </>
          )}
          {record.status === 'PENDENTE' && (
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          )}
          {isAdmin && record.status !== 'PENDENTE' && (
            <Popconfirm
              title="Tem certeza que deseja reverter esta validação? O status voltará para PENDENTE."
              onConfirm={() => handleRevert(record)}
              okText="Sim"
              cancelText="Não"
            >
              <Button
                icon={<SyncOutlined />}
                size="small"
                type="default"
                title="Reverter"
              >
                Reverter
              </Button>
            </Popconfirm>
          )}
          {isAdmin && (
            <Popconfirm
              title="Tem certeza que deseja excluir esta aula?"
              onConfirm={() => handleDelete(record.id)}
              okText="Sim"
              cancelText="Não"
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                title="Excluir"
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Aulas Pendentes"
              value={stats.pendentes}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Aulas Validadas"
              value={stats.validadas}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Aulas Rejeitadas"
              value={stats.rejeitadas}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtros */}
      <Card 
        title={
          <Space>
            <FilterOutlined />
            Filtros de Busca
            <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#888' }}>
              ({aulasFiltradas.length} de {aulas.length} aulas)
            </span>
          </Space>
        }
        extra={
          <Button type="text" size="small" onClick={() => setMostrarFiltros(!mostrarFiltros)}>
            {mostrarFiltros ? 'Ocultar' : 'Mostrar'}
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        {mostrarFiltros && (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Professor</div>
              <Select
                placeholder="Todos os professores"
                allowClear
                style={{ width: '100%' }}
                value={filtros.professor}
                onChange={(value) => handleFiltroChange('professor', value)}
                showSearch
                optionFilterProp="children"
              >
                {professores.map((prof) => (
                  <Select.Option key={prof.id} value={prof.id}>
                    {prof.nome}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Turma</div>
              <Select
                placeholder="Todas as turmas"
                allowClear
                style={{ width: '100%' }}
                value={filtros.turma}
                onChange={(value) => handleFiltroChange('turma', value)}
                showSearch
                optionFilterProp="children"
              >
                {getTurmasPorProfessorFiltro().map((turma) => (
                  <Select.Option key={turma.id} value={turma.id}>
                    {turma.nome_turm}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Status</div>
              <Select
                placeholder="Todos os status"
                allowClear
                style={{ width: '100%' }}
                value={filtros.status}
                onChange={(value) => handleFiltroChange('status', value)}
              >
                <Select.Option value="PENDENTE">Pendente</Select.Option>
                <Select.Option value="VALIDADA">Validada</Select.Option>
                <Select.Option value="REJEITADA">Rejeitada</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Data da Aula</div>
              <RangePicker
                placeholder={['Data inicial', 'Data final']}
                value={
                  filtros.data_inicial || filtros.data_final
                    ? [
                        filtros.data_inicial ? dayjs(filtros.data_inicial) : null,
                        filtros.data_final ? dayjs(filtros.data_final) : null,
                      ]
                    : null
                }
                onChange={handlePeriodoChange}
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>&nbsp;</div>
              <Button icon={<ClearOutlined />} onClick={limparFiltros} block>
                Limpar Filtros
              </Button>
            </Col>
          </Row>
        )}
      </Card>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Registrar Aula
          </Button>
          {isAdmin && selectedRowKeys.length > 0 && (
            <Button danger onClick={handleBulkDelete}>
              Excluir Selecionadas ({selectedRowKeys.length})
            </Button>
          )}
          {isAdmin && (
            <Popconfirm
              title="Apagar todas as aulas pendentes da turma selecionada?"
              description="Aulas validadas serão mantidas. Esta ação não pode ser desfeita."
              onConfirm={handleDeletePendentesTurma}
              okText="Apagar"
              cancelText="Cancelar"
              disabled={!filtros.turma}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={bulkDeleting}
                disabled={!filtros.turma}
              >
                Apagar pendentes da turma
              </Button>
            </Popconfirm>
          )}
        </Space>
      </div>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={aulasFiltradas}
        loading={loading}
        rowKey="id"
        pagination={{ 
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total de ${total} aulas`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
      />

      {/* Modal de Registro/Edição */}
      <Modal
        title={editingId ? 'Editar Aula' : 'Registrar Aula'}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="id_equipe"
            label="Professor"
            rules={[{ required: true, message: 'Selecione o professor' }]}
          >
            <Select
              placeholder="Selecione o professor"
              onChange={handleProfessorChange}
              showSearch
              optionFilterProp="children"
            >
              {professores.map((prof) => (
                <Select.Option key={prof.id} value={prof.id}>
                  {prof.nome}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.id_equipe !== currentValues.id_equipe
            }
          >
            {({ getFieldValue }) => {
              const professorId = getFieldValue('id_equipe');
              const turmasFiltradas = getTurmasPorProfessor(professorId);
              return (
                <Form.Item
                  name="id_turma"
                  label="Turma"
                  rules={[{ required: true, message: 'Selecione a turma' }]}
                >
                  <Select
                    placeholder={
                      professorId
                        ? 'Selecione a turma'
                        : 'Primeiro selecione um professor'
                    }
                    disabled={!professorId}
                    onChange={handleTurmaChange}
                  >
                    {turmasFiltradas.map((turma) => (
                      <Select.Option key={turma.id} value={turma.id}>
                        {turma.nome_turm}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            name="data_aula"
            label="Data da Aula"
            rules={[{ required: true, message: 'Informe a data da aula' }]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="hora_inicio"
                label="Hora Início"
                rules={[{ required: true, message: 'Informe a hora' }]}
              >
                <TimePicker
                  format="HH:mm"
                  style={{ width: '100%' }}
                  onChange={handleHorarioChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="hora_fim"
                label="Hora Fim"
                rules={[{ required: true, message: 'Informe a hora' }]}
              >
                <TimePicker
                  format="HH:mm"
                  style={{ width: '100%' }}
                  onChange={handleHorarioChange}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="duracao_horas" label="Duração (horas)">
            <InputNumber
              min={0}
              step={0.5}
              placeholder="Calculado automaticamente"
              style={{ width: '100%' }}
              disabled
            />
          </Form.Item>

          <Form.Item name="observacoes" label="Observações">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Validação */}
      <Modal
        title="Validar Aula"
        open={isValidateModalVisible}
        onOk={() => validateForm.submit()}
        onCancel={() => setIsValidateModalVisible(false)}
      >
        <Form
          form={validateForm}
          layout="vertical"
          onFinish={handleValidateSubmit}
        >
          <Form.Item
            name="status"
            label="Decisão"
            rules={[{ required: true, message: 'Selecione uma opção' }]}
          >
            <Select placeholder="Selecione">
              <Select.Option value="VALIDADA">Validar Aula</Select.Option>
              <Select.Option value="VALIDAR_AJUSTAR">Validar e Ajustar horário</Select.Option>
              <Select.Option value="REJEITADA">Rejeitar Aula</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item shouldUpdate={(prev, cur) => prev.status !== cur.status}>
            {({ getFieldValue }) =>
              getFieldValue('status') === 'VALIDAR_AJUSTAR' ? (
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="hora_inicio"
                      label="Hora Início"
                      rules={[{ required: true, message: 'Informe a hora de início' }]}
                    >
                      <TimePicker format="HH:mm" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="hora_fim"
                      label="Hora Fim"
                      rules={[{ required: true, message: 'Informe a hora de fim' }]}
                    >
                      <TimePicker format="HH:mm" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              ) : null
            }
          </Form.Item>

          <Form.Item name="observacoes" label="Observações">
            <TextArea rows={3} placeholder="Motivo da rejeição (opcional)" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AulasDadasList;
